import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { PrivateProfileDto, ProfileStatsDto } from '@redpandev/shared';
import { Card, Spinner } from '@redpandev/ui';
import { api, type ActiveMatch } from '../lib/api';
import { StatsBlock } from '../components/StatsBlock';
import { ProfileHero } from '../components/profile/ProfileHero';
import { ActiveMatchesList } from '../components/profile/ActiveMatchesList';
import { HistoryTimeline } from '../components/profile/HistoryTimeline';
import { LinkButton } from '../components/profile/LinkButton';
import { ShareProfileCard } from '../components/profile/ShareProfileCard';
import { useHistory } from '../components/profile/useHistory';

/**
 * La maison du joueur (spec 08 §3) : héro (avatar + badges), parties en cours
 * (action n°1), stats, partage, historique tronqué. La gestion du compte vit
 * sous /settings — trois portes d'entrée au libellé unique « Paramètres » :
 * ⚙️ près du héro, menu avatar du header, carte en pied de page.
 * Mobile : tout empilé ; ≥lg : héro pleine largeur puis 2 colonnes.
 */
export function ProfilePage() {
  const [profile, setProfile] = useState<PrivateProfileDto | null>(null);
  const [stats, setStats] = useState<ProfileStatsDto | null>(null);
  const [activeMatches, setActiveMatches] = useState<ActiveMatch[] | null>(null);
  const [error, setError] = useState<string>();
  // Historique paginé (endpoint public) : chargé dès que le pseudo est connu.
  const history = useHistory(profile?.displayName);

  useEffect(() => {
    let alive = true;
    Promise.all([api.profileMe(), api.myStats()])
      .then(([p, s]) => {
        if (alive) {
          setProfile(p);
          setStats(s);
        }
      })
      .catch(() => alive && setError('Impossible de charger le profil.'));
    return () => {
      alive = false;
    };
  }, []);

  // Parties en cours : indépendant du profil (un échec ici ne doit pas masquer la page).
  useEffect(() => {
    let alive = true;
    api
      .activeMatches()
      .then((r) => alive && setActiveMatches(r.matches))
      .catch(() => alive && setActiveMatches([]));
    return () => {
      alive = false;
    };
  }, []);

  if (error)
    return (
      <main className="mx-auto max-w-lg px-6 py-20 text-center">
        <p role="alert" className="text-danger-500">
          {error}
        </p>
      </main>
    );
  if (!profile || !stats)
    return (
      <main className="grid place-items-center px-6 py-24">
        <Spinner size="lg" label="Chargement du profil…" />
      </main>
    );

  return (
    <main className="mx-auto max-w-content px-4 py-8 sm:px-6 sm:py-12">
      <div className="relative">
        <ProfileHero
          displayName={profile.displayName}
          avatarId={profile.avatarId}
          bio={profile.bio}
          createdAt={profile.createdAt}
          email={profile.email}
          emailVerified={profile.emailVerified}
          pendingEmail={profile.pendingEmail}
        />
        <Link
          to="/settings"
          aria-label="Paramètres"
          title="Paramètres"
          className="absolute right-0 top-0 inline-flex h-11 w-11 items-center justify-center rounded-full border border-ink-600/60 bg-ink-700/60 text-lg transition-[border-color,box-shadow] duration-fast ease-fun hover:border-panda-500/60 hover:shadow-glow-panda"
        >
          <span aria-hidden="true">⚙️</span>
        </Link>
      </div>

      <div className="mt-8 grid gap-8 sm:mt-10 lg:grid-cols-2 lg:items-start">
        <div className="space-y-8">
          <section aria-labelledby="active-matches-title">
            <h2
              id="active-matches-title"
              className="mb-3 font-display text-xl font-bold text-ink-100"
            >
              Parties en cours
            </h2>
            <ActiveMatchesList matches={activeMatches} limit={3} />
          </section>

          <section aria-labelledby="stats-title">
            <h2 id="stats-title" className="mb-3 font-display text-xl font-bold text-ink-100">
              Tes stats
            </h2>
            <StatsBlock stats={stats} owner collapsibleDetails />
          </section>

          <ShareProfileCard displayName={profile.displayName} />
        </div>

        <section aria-labelledby="history-title">
          <h2 id="history-title" className="mb-3 font-display text-xl font-bold text-ink-100">
            Historique
          </h2>
          <HistoryTimeline
            items={history.items}
            error={history.error}
            limit={5}
            hasMore={history.hasMore}
            loadingMore={history.loadingMore}
            onLoadMore={history.loadMore}
          />
        </section>
      </div>

      {/* Anti-désorientation (spec 08 §4.4) : « Ton compte » a longtemps vécu
          ici en bas — on y laisse une porte pérenne vers les Paramètres. */}
      <section aria-label="Paramètres" className="mt-8">
        <Card className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-bold text-ink-100">Paramètres</h2>
            <p className="mt-1 text-sm text-ink-400">
              Pseudo &amp; avatar, email, mot de passe, préférences d&apos;emails.
            </p>
          </div>
          <LinkButton to="/settings" variant="secondary">
            Ouvrir les paramètres →
          </LinkButton>
        </Card>
      </section>
    </main>
  );
}
